import { ISessionScheduleInfo, IEventAttendeeInfo } from '@interfaces/CalendarEvents'
import { SCHEDULE_STATUS } from '@utils/constants'
import { ATTENDEE_STATUS } from '@utils/constants/events'
import { EventAttributes, Alarm, DurationObject, Attendee } from 'ics'
import moment from 'moment'

type NumberArray = [number, number, number, number, number]

class PortalCalendarEvent {
  calendarEvent: ISessionScheduleInfo | IEventAttendeeInfo
  attendees: Attendee[]

  constructor(calendarEvent: ISessionScheduleInfo | IEventAttendeeInfo, attendees: Attendee[]) {
    this.calendarEvent = calendarEvent
    this.attendees = attendees
  }

  get ICS(): EventAttributes {
    return {
      start: this.getICSDate('start'),
      duration: this.Duration,
      uid: this.calendarEvent.RecordId,
      title: this.Title,
      organizer: {
        name: 'Berlitz',
        email: 'noreply@languages.berlitz.com',
      },
      sequence: parseInt(moment.utc().format('X')),
      description: `${this.Title}\n\n${process.env.SITE_URL}`,
      url: process.env.SITE_URL,
      location: 'Online',
      lastModified: this.LastModified,
      alarms: [], // removing alarms since it has different behavior on different email/calendar clients
      attendees: this.attendees,
    }
  }

  get Duration(): DurationObject {
    let hours = 0
    let minutes = 0

    if (this.isSessionScheduleInfo(this.calendarEvent)) {
      const SessionLengthInMinutes = this.calendarEvent.SessionLengthInMinutes!
      hours = Math.floor(SessionLengthInMinutes / 60)
      minutes = SessionLengthInMinutes! % 60
    }

    if (this.isEventAttendeeInfo(this.calendarEvent)) {
      const DurationInHours = parseFloat(this.calendarEvent.DurationInHours!)
      const DurationInMinutes = DurationInHours * 60
      const duration = moment().startOf('day').add(DurationInMinutes, 'minutes')
      hours = duration.hours()
      minutes = duration.minutes()
    }
    return {
      hours,
      minutes,
    }
  }

  get Title() {
    if (this.isSessionScheduleInfo(this.calendarEvent)) {
      return `${this.TitlePrefix}Berlitz ${this.calendarEvent.ProgramLanguage} Lesson`
    }

    if (this.isEventAttendeeInfo(this.calendarEvent)) {
      return `${this.TitlePrefix}Berlitz ${this.calendarEvent.EventType}`
    }

    return `Berlitz Calendar Event`
  }

  get TitlePrefix() {
    const status = this.Status
    return status === SCHEDULE_STATUS.CANCELLED
      ? `(${SCHEDULE_STATUS.CANCELLED}) `
      : status === SCHEDULE_STATUS.RESCHEDULED
        ? `(${SCHEDULE_STATUS.RESCHEDULED}) `
        : ''
  }

  get Status() {
    if (this.isSessionScheduleInfo(this.calendarEvent)) {
      return this.calendarEvent.Status
    }

    if (this.isEventAttendeeInfo(this.calendarEvent)) {
      return this.calendarEvent.AttendeeStatus
    }

    return ''
  }

  get LastModified(): NumberArray {
    const m = moment.utc()
    return [m.year(), m.month() + 1, m.date(), m.hour(), m.minute()]
  }

  get Alarms(): Alarm[] {
    return [
      {
        action: 'audio',
        repeat: 2,
        duration: {
          minutes: 10,
        },
        attachType: 'VALUE=URI',
        attach: 'Glass',
        trigger: {
          before: true,
          hours: 1,
        },
      },
      {
        action: 'display',
        trigger: {
          before: true,
          minutes: 15,
        },
      },
      {
        action: 'email',
        duration: {
          minutes: 10,
        },
        trigger: {
          before: true,
          minutes: 30,
        },
      },
    ]
  }

  get IsCancelled() {
    return this.Status === SCHEDULE_STATUS.CANCELLED || this.Status === ATTENDEE_STATUS.CANCELLED
  }

  get IsRescheduled() {
    return this.Status === SCHEDULE_STATUS.RESCHEDULED
  }

  getICSDate(key: 'start' | 'end'): NumberArray {
    let dt = ''
    if (this.isSessionScheduleInfo(this.calendarEvent)) {
      dt = this.calendarEvent[key === 'start' ? 'StartTime' : 'EndTime']!
    }
    if (this.isEventAttendeeInfo(this.calendarEvent)) {
      dt = this.calendarEvent[key === 'start' ? 'Starttime' : 'EndTime']!
    }
    const m = moment(dt)
    return [m.year(), m.month() + 1, m.date(), m.hour(), m.minute()]
  }

  isSessionScheduleInfo(obj: any): obj is ISessionScheduleInfo {
    return 'ProgramDeliveryCenter' in obj
  }

  isEventAttendeeInfo(obj: any): obj is IEventAttendeeInfo {
    return 'AttendeeStatus' in obj
  }
}

export default PortalCalendarEvent
